/**
 * SQL Format Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type Dialect = 'sql' | 'postgresql' | 'mysql' | 'sqlite' | 'tsql';

export const SqlFormatTool: ITransformerTool = {
  id: 'sql-format',
  name: 'SQL Format',
  description: 'Format SQL queries for readability',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['sql', 'format', 'beautify', 'prettify'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'language',
      type: 'select',
      label: 'Dialect',
      defaultValue: 'sql',
      options: ['sql', 'postgresql', 'mysql', 'sqlite', 'tsql'],
      description: 'SQL dialect for formatting rules',
    },
    {
      name: 'tabWidth',
      type: 'number',
      label: 'Indent Size',
      defaultValue: 2,
      description: 'Number of spaces to indent',
      validation: { min: 1, max: 8, step: 1 },
    },
    {
      name: 'uppercase',
      type: 'boolean',
      label: 'Uppercase Keywords',
      defaultValue: true,
      description: 'Uppercase SQL keywords like SELECT, FROM',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const language = (config.language as Dialect) || 'sql';
    const tabWidth = typeof config.tabWidth === 'number' ? (config.tabWidth as number) : 2;
    const keywordCase = config.uppercase === false ? 'preserve' : 'upper';

    try {
      const { format: formatSql } = await import('sql-formatter');
      return formatSql(String(input ?? ''), {
        language,
        indent: ' '.repeat(tabWidth),
        keywordCase,
      } as Parameters<typeof formatSql>[1]);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to format SQL';
      throw ToolError.execution(message);
    }
  },
};
